import { faChevronDown, faChevronUp } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import { useState } from "react";
import type {
    EmissionEvent,
    EmissionEventFull,
    EmissionRecordView,
    Pipeline,
    PipelineSegment,
    Site,
} from "../../../../../apiClient/generated";
import {
    AlignedDataDisplay,
    CoordinatesField,
} from "../../../../../ui/GenericFields";
import { formatDate, formatDateTime } from "../../../../../utils/dates";
import { EventStatusV2Display } from "../../../../EventsV2/EventStatusDisplay";

interface EventSidebarDisplayProps {
    event: EmissionEvent | EmissionEventFull | undefined;
}

export const EventSidebarDisplay = (props: EventSidebarDisplayProps) => (
    <AlignedDataDisplay
        data={[
            {
                label: "Status",
                value: (
                    <EventStatusV2Display
                        eventStatus={props.event?.eventStatus}
                    />
                ),
            },
            {
                label: "Root cause",
                value: props.event?.formData.ae_root_cause,
            },
            props.event?.responsibleName && {
                label: "Assignee",
                value: props.event.responsibleName,
            },
            props.event?.reviewerName && {
                label: "Coordinator",
                value: props.event.reviewerName,
            },
            props.event?.formData.ae_start_date && {
                label: "Start time",
                value: formatDateTime(props.event.formData.ae_start_date),
            },
            props.event?.formData.ae_end_date && {
                label: "End time",
                value: formatDateTime(props.event.formData.ae_end_date),
            },
            props.event?.formData.ae_notes && {
                label: "Notes",
                value: props.event.formData.ae_notes,
            },
            props.event?.updatedAt && {
                label: "Last update",
                value: formatDateTime(props.event.updatedAt),
            },
            props.event?.createdAt && {
                label: "Created at",
                value: formatDate(props.event.createdAt),
            },
        ].filter(Boolean)}
    />
);

interface EmissionSidebarDisplayProps {
    emission: EmissionRecordView;
}

export const EmissionSidebarDisplay = (props: EmissionSidebarDisplayProps) => (
    <AlignedDataDisplay
        data={[
            {
                label: "Rate",
                value: (
                    <div>
                        {props.emission.dataPoint.detectedRate / 1000} kg/h
                    </div>
                ),
            },
            {
                label: "Provider",
                value: <div>{props.emission.providerName}</div>,
            },
            {
                label: "Time UTC",
                value: formatDateTime(
                    props.emission.dataPoint.detectionTimestamp,
                ),
            },
        ]}
    />
);

interface InfrastructureSidebarDisplayProps {
    infrastructure: any;
}

export const InfrastructureSidebarDisplay = (
    props: InfrastructureSidebarDisplayProps,
) => {
    const [expand, setExpand] = useState(false);

    if (!props.infrastructure) {
        return null;
    }

    const data = [
        {
            label: "Name",
            value:
                props.infrastructure.siteName ||
                props.infrastructure.name ||
                props.infrastructure.equipmentType,
        },
        {
            label: "Location",
            value: (
                <CoordinatesField
                    coordinates={props.infrastructure.location?.coordinates}
                    allowCopy
                />
            ),
        },
        {
            label: "Type",
            value: props.infrastructure.infraType.replaceAll("_", " "),
        },
    ];

    if (expand) {
        Object.entries(props.infrastructure.extraData || {})
            .toSorted((a, b) => a[0].localeCompare(b[0]))
            .map(([key, value]) => {
                let cleanValue: string;
                if (typeof value === "string") {
                    cleanValue = value;
                } else if (typeof value === "number") {
                    cleanValue = value.toString();
                } else if (typeof value === "object") {
                    cleanValue = JSON.stringify(value);
                }
                if (cleanValue) {
                    data.push({
                        label: key,
                        value: cleanValue,
                    });
                } else {
                    Sentry.captureException(
                        `Could not convert extra_data value to string on site ${props.infrastructure.id}, property name ${key}.`,
                    );
                }
            });
    }

    return (
        <div>
            <AlignedDataDisplay data={data} useGrid />
            <button
                type="button"
                className="ml-1 text-xs underline"
                onClick={() => setExpand(!expand)}
            >
                {expand ? "Hide" : "Show"} details
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className="h-3 w-3 text-black pl-1"
                />
            </button>
        </div>
    );
};

interface SiteSidebarDisplayProps {
    site: Site;
}

export const SiteSidebarDisplay = (props: SiteSidebarDisplayProps) => {
    const [expand, setExpand] = useState(false);

    if (!props.site) {
        return null;
    }

    const data = [
        {
            label: "Name",
            value: props.site.siteName,
        },
        {
            label: "Operator Unique ID",
            value: props.site.operatorUniqueId,
        },
        {
            label: "Location",
            value: (
                <CoordinatesField
                    coordinates={props.site.location?.coordinates}
                    allowCopy
                />
            ),
        },
        props.site.siteTypeFromImagery !== ""
            ? {
                  label: "Site Type (from imagery)",
                  value: (
                      <span className="capitalize">
                          {props.site.siteTypeFromImagery.replaceAll("_", " ")}
                      </span>
                  ),
              }
            : undefined,
    ];

    if (expand) {
        data.push({
            label: "Subpart W Segment",
            value: props.site.subpartWSegment,
        });
        data.push({
            label: "Other Operator IDs",
            value: props.site.otherOperatorIds,
        });
        data.push({
            label: "Status",
            value: props.site.operatorStatus,
        });
        data.push({
            label: "Data Source",
            value: props.site.siteDataSource,
        });
        data.push({
            label: "Start Date",
            value: props.site.startDate && formatDate(props.site.startDate),
        });
        data.push({
            label: "End Date",
            value: props.site.endDate && formatDate(props.site.endDate),
        });
        Object.entries(props.site.extraData || {})
            .toSorted((a, b) => a[0].localeCompare(b[0]))
            .map(([key, value]) => {
                let cleanValue: string;
                if (typeof value === "string") {
                    cleanValue = value;
                } else if (typeof value === "number") {
                    cleanValue = value.toString();
                } else if (typeof value === "object") {
                    cleanValue = JSON.stringify(value);
                }
                if (cleanValue) {
                    data.push({
                        label: key,
                        value: cleanValue,
                    });
                } else {
                    Sentry.captureException(
                        `Could not convert extra_data value to string on site ${props.site.id}, property name ${key}.`,
                    );
                }
            });
    }

    return (
        <div>
            <AlignedDataDisplay data={data} useGrid />
            <button
                type="button"
                className="mt-2 text-xs underline"
                onClick={() => setExpand(!expand)}
            >
                {expand ? "Hide" : "Show"} details
                <FontAwesomeIcon
                    icon={expand ? faChevronUp : faChevronDown}
                    className="h-3 w-3 text-black pl-1"
                />
            </button>
        </div>
    );
};

export const PipelineSidebarDisplay = ({ pipeline }: { pipeline: Pipeline }) =>
    pipeline && (
        <AlignedDataDisplay
            data={[
                {
                    label: "Line ID",
                    value: pipeline.lineId,
                },
                {
                    label: "Owner",
                    value: pipeline.ownerName,
                },
                {
                    label: "Product",
                    value: (
                        <span className="capitalize">
                            {pipeline.pipelineProduct
                                .replaceAll("_", " ")
                                .toLocaleLowerCase()}
                        </span>
                    ),
                },
                {
                    label: "Type:",
                    value: (
                        <span className="capitalize">
                            {pipeline.pipelineType
                                .replaceAll("_", " ")
                                .toLocaleLowerCase()}
                        </span>
                    ),
                },
            ]}
        />
    );

export const PipelineSegmentSidebarDisplay = ({
    segment,
}: {
    segment: PipelineSegment;
}) => {
    const [showExtra, setShowExtra] = useState(false);

    if (!segment) {
        return null;
    }

    const data = [
        {
            label: "Material",
            value: segment.material,
        },
        {
            label: "Diameter (inches)",
            value: segment.diameter,
        },
        {
            label: "Above ground",
            value: segment.aboveGround ? "Yes" : "No",
        },
    ];

    return (
        <div className="h-full text-sm">
            <AlignedDataDisplay data={data} />
            {segment.extraData && (
                <button
                    type="button"
                    className="p-1.5 my-0.5 rounded hover:bg-ae-slate-200"
                    onClick={() => setShowExtra(!showExtra)}
                >
                    {showExtra
                        ? "Hide extra parameters"
                        : "Show extra parameters"}
                </button>
            )}
            {showExtra && segment.extraData && (
                <AlignedDataDisplay
                    data={Object.entries(segment.extraData).map(
                        ([key, value]) => ({
                            label: key,
                            value: JSON.stringify(value),
                        }),
                    )}
                />
            )}
        </div>
    );
};
